<template lang="pug">
  div(class="documents-sign-status")
    template(v-if="showResult")
      block-documents-sign-results(
        :icon="resultIcon"
        :title="resultTitle"
        :failed-documents="failedDocuments"
        :close-modal="closeModal"
        :is-inside-modal="isInsideModal"
        @back="showResult = false"
      )
    template(v-else)
      h2(class="documents-sign-status__title") {{ title }}
      div(class="documents-sign-status__content")
        block-documents-sign-list(
          :documents="documents"
          :documents-states="documentsStates"
          :statuses="documentStatuses"
          :is-eds-and-stamp-uploaded="isEdsAndStampUploaded"
          :is-stamp="isStamp"
        )
      div(class="documents-sign-status__actions")
        v-btn(
          v-if="showStop"
          class="secondary-button error-btn w-100 documents-sign-status__action"
          @click="stopSign"
        ) {{ $t('Stop sign') }}
        template(v-else)
          v-btn(
            v-if="showButton"
            class="main-button documents-sign-status__action"
            @click="signDocuments"
          ) {{ $t('Continue') }}
</template>

<script>
import Base64js from 'base64-js'
import BlockDocumentsSignResults from '~/modules/documents/components/blocks/block-documents-sign-results'
import BlockDocumentsSignList from '~/modules/documents/components/blocks/block-documents-sign-list'
import getEdsIitInterfaceOutside from '~/modules/eds-iit-checkbox/mixins/getEdsIitInterfaceOutside'

export default {
  name: 'MDocumentsSignStatus',
  components: {
    BlockDocumentsSignList,
    BlockDocumentsSignResults
  },
  mixins: [getEdsIitInterfaceOutside],
  props: {
    closeModal: {
      type: Function,
      default: () => {}
    },
    changeDialogWidth: {
      type: Function,
      default: () => {}
    },
    documents: {
      type: Array,
      default: () => []
    },
    sign: {
      type: Function,
      default: () => {}
    },
    // TODO: remove it after fix dialogs padding (t-dialog.vue styles)
    isInsideModal: {
      type: Boolean,
      default: false
    },
    signOnCreate: {
      type: Boolean,
      default: false
    },
    isDiia: {
      type: Boolean,
      default: false
    },
    isSmartId: {
      type: Boolean,
      default: false
    },
    isMono: {
      type: Boolean,
      default: false
    },
    isCloudProvider: {
      type: Boolean,
      default: false
    },
    signedDocuments: {
      type: Array,
      default: () => []
    },
    isEdsAndStampUploaded: {
      type: Boolean,
      default: false
    },
    handleRereadStampKey: {
      type: Function,
      default: () => {}
    },
    isStamp: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isStopped: false,
      showStop: false,
      showResult: false,
      documentsStates: {}
    }
  },
  computed: {
    title() {
      return !this.showStop
        ? this.$t('List of documents for signature')
        : this.$t('Mass documents sign')
    },
    failedDocuments() {
      const failedDocuments = []
      Object.entries(this.documentsStates).filter(([id, value]) => {
        if (
          this._.get(value, 'status') === this.documentStatuses.error ||
          this._.get(value, 'stampStatus') === this.documentStatuses.error
        ) {
          const document = this._.find(this.documents, document => document.id === id)
          failedDocuments.push({
            title: this._.get(document, 'title'),
            reason: this._.get(value, 'error'),
            stampReason: this._.get(value, 'stampError')
          })
        }
      })
      return failedDocuments
    },
    documentStatuses() {
      return {
        signed: 'signed',
        skipped: 'skipped',
        error: 'error',
        created: 'created'
      }
    },
    showButton() {
      const passedDocuments = this._.filter(this.documentsStates, document => document.passed)
      return this._.get(passedDocuments, 'length') < this._.get(this.documents, 'length')
    },
    isAllDocumentsFailed() {
      return Object.values(this.documentsStates).every(obj => {
        return obj.status === this.documentStatuses.error
      })
    },
    resultIcon() {
      if (this.isAllDocumentsFailed) {
        return require('~/assets/images/warning.png')
      }
      return require('~/assets/images/party-popper.png')
    },
    resultTitle() {
      if (this.isAllDocumentsFailed) {
        return this.$t('Documents not signed')
      }
      return this.$t('Documents signed')
    }
  },
  watch: {
    showResult(val) {
      if (val) {
        if (this.isInsideModal) {
          this.changeDialogWidth('512px')
          return
        }
        this.changeWrapperWidth('512px')
      } else {
        if (this.isInsideModal) {
          this.changeDialogWidth('724px')
          return
        }
        this.changeWrapperWidth('724px')
      }
    }
  },
  beforeDestroy() {
    if (this.isInsideModal) {
      this.changeDialogWidth('724px')
    }
  },
  created() {
    for (const doc of this.documents) {
      const documentState = {
        loading: false,
        stampLoading: false,
        error: null,
        stampError: null,
        status: this.documentStatuses.created,
        stampStatus: this.documentStatuses.created,
        passed: false,
        stampPassed: false
      }
      this.$set(this.documentsStates, doc.id, documentState)
    }
    if (this.signOnCreate) {
      if (this.isDiia || this.isSmartId || this.isMono || this.isCloudProvider) {
        this.signListOfDocumentsByDubidoc(this.signedDocuments)
      } else {
        this.signDocumentsByEdsKey(this.sign)
      }
    }
  },
  methods: {
    async signDocuments() {
      const edsIitInterface = await this.getEdsIitSignInterface(this.documents)
      if (!edsIitInterface) {
        return
      }
      const { isDiia, isSmartId, isCloudProvider, isMono, sign, onConfirmData } = edsIitInterface
      if (isDiia || isMono || isSmartId || isCloudProvider) {
        await this.signListOfDocumentsByDubidoc(onConfirmData)
      } else {
        await this.signDocumentsByEdsKey(sign)
      }
    },
    getDocumentFileHash(document) {
      if ('fileHash' in document) {
        return Base64js.toByteArray(this._.get(document, 'fileHash', ''))
      }
      return Base64js.toByteArray(this._.get(document, 'file', ''))
    },
    getDocumentStateFields(document, type) {
      if (type === 'eds') {
        return {
          setLoading: value => {
            this.documentsStates[document.id].loading = value
          },
          setStatus: status => {
            this.documentsStates[document.id].status = status
          },
          setError: error => {
            this.documentsStates[document.id].error = error
          }
        }
      } else if (type === 'stamp') {
        return {
          setLoading: value => {
            this.documentsStates[document.id].stampLoading = value
          },
          setStatus: status => {
            this.documentsStates[document.id].stampStatus = status
          },
          setError: error => {
            this.documentsStates[document.id].stampError = error
          }
        }
      }
    },
    async signDocumentsByType(signFn, isStamp, msg) {
      for (const document of this.documents) {
        const { setLoading, setStatus, setError } = this.getDocumentStateFields(
          document,
          isStamp ? 'stamp' : 'eds'
        )
        if (this.isStopped) {
          if (this.isInsideModal) {
            this.closeModal(true)
          }
          return
        }
        try {
          setLoading(true)
          let updatedDocument = document
          if (document.signatureType === 'internal' && isStamp) {
            updatedDocument = await this.$store.dispatch('documents/read', {
              id: document.id
            })
          }
          const file = this.getDocumentFileHash(updatedDocument)
          const signature = await signFn(file)
          if (!signature) {
            return
          }
          await this.$store.dispatch('documents/sign', {
            id: updatedDocument.id,
            payload: {
              signature,
              isStamp
            }
          })
          setStatus(this.documentStatuses.signed)
          this.$notification.success(msg)
        } catch (e) {
          setError(this._.get(e, 'response.data.detail') || this._.get(e, 'response.data.message'))
          setStatus(this.documentStatuses.error)
        } finally {
          this.documentsStates[document.id].passed = true
          setLoading(false)
        }
      }
    },
    async signDocumentsByEdsKey(signFn) {
      this.showStop = true
      if (this.isEdsAndStampUploaded) {
        // loop all document and sign with eds key
        await this.signDocumentsByType(
          signFn,
          false,
          this.$t('Documents has been signed successfully')
        )
        // changing from eds key to stamp key
        await this.handleRereadStampKey()
        // loop all document and sign with stamp key
        await this.signDocumentsByType(
          signFn,
          true,
          this.$t('Documents has been signed with stamp successfully')
        )
      } else {
        await this.signDocumentsByType(
          signFn,
          this.isStamp,
          this.$t('Documents has been signed successfully')
        )
      }
      if (!this.isInsideModal) {
        this.$emit('success')
      }
      this.showResult = true
      this.showStop = false
    },
    async signListOfDocumentsByDubidoc(documents) {
      this.showStop = true
      let failedDocuments = []
      const successfulDocuments = Array.isArray(documents) ? [...documents] : []
      const errorInfoIndex = successfulDocuments.findIndex(item => item && item.__isErrorInfo)
      if (errorInfoIndex !== -1) {
        const errorInfo = successfulDocuments[errorInfoIndex]
        failedDocuments = errorInfo._failedDocuments || []
        successfulDocuments.splice(errorInfoIndex, 1)
      }
      for (const failedDoc of failedDocuments) {
        const documentId = failedDoc.externalId
        if (documentId && this.documentsStates[documentId]) {
          this.documentsStates[documentId].error =
            failedDoc.error || 'Не вдалося підписати документ'
          this.documentsStates[documentId].status = this.documentStatuses.error
          this.documentsStates[documentId].passed = true
          this.documentsStates[documentId].loading = false
        }
      }
      for (let i = 0; i < successfulDocuments.length; i++) {
        const signedDocument = successfulDocuments[i]
        const documentId =
          this._.get(signedDocument, 'externalId') || this._.get(this.documents[i], 'id')
        if (!this.documentsStates[documentId]) {
          continue
        }
        try {
          this.documentsStates[documentId].loading = true
          const signature = this._.get(signedDocument, 'signature') || signedDocument
          await this.$store.dispatch('documents/sign', {
            id: documentId,
            payload: {
              signature
            }
          })
          this.documentsStates[documentId].status = this.documentStatuses.signed
          this.$notification.success(this.$t('Documents has been signed successfully'))
        } catch (e) {
          this.documentsStates[documentId].error =
            this._.get(e, 'response.data.detail') || this._.get(e, 'response.data.message')
          this.documentsStates[documentId].status = this.documentStatuses.error
        } finally {
          this.documentsStates[documentId].passed = true
          this.documentsStates[documentId].loading = false
        }
      }
      if (!this.isInsideModal) {
        this.$emit('success')
      }
      this.showResult = true
      this.showStop = false
    },
    stopSign() {
      this.isStopped = true
    },
    changeWrapperWidth(width) {
      this.$emit('changeWidth', width)
    }
  }
}
</script>

<style scoped lang="scss">
.documents-sign-status {
  &__title {
    color: #000;
    font-size: 32px;
    font-weight: 700;
    line-height: 40px;
    text-align: left;
    margin-bottom: 40px;
  }

  &__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding-top: 32px;
  }

  &__action {
    grid-column: 2 / 3;
  }
}
</style>
